import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { api } from "@/lib/api";
import { buildLabelsPdf } from "@/lib/labelPdf";
import { printLabelsHtml } from "@/lib/printLabels";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import RollLabel from "@/components/RollLabel";
import { Printer, QrCode, Barcode as BarcodeIcon, X, DownloadSimple } from "@phosphor-icons/react";

export default function PrintLabelsDialog({ open, onOpenChange, rollIds }) {
  const [rolls, setRolls] = useState([]);
  const [products, setProducts] = useState({});
  const [manufacturers, setManufacturers] = useState({});
  const [series, setSeries] = useState({});
  const [codeType, setCodeType] = useState("qr"); // "qr" | "barcode"
  const [qrImages, setQrImages] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !rollIds?.length) return;
    (async () => {
      const rs = await Promise.all(rollIds.map((id) => api.get(`/rolls/${id}`).then((r) => r.data).catch(() => null)));
      const fetched = rs.filter(Boolean);
      setRolls(fetched);
      const productIds = [...new Set(fetched.map((r) => r.product_id))];
      const productResults = await Promise.all(productIds.map((pid) => api.get(`/products/${pid}`).then((r) => r.data).catch(() => null)));
      const productMap = {};
      productResults.filter(Boolean).forEach((p) => { productMap[p.id] = p; });
      setProducts(productMap);
      const [{ data: ms }, { data: ss }] = await Promise.all([api.get("/manufacturers"), api.get("/series")]);
      setManufacturers(Object.fromEntries(ms.map((m) => [m.id, m])));
      setSeries(Object.fromEntries(ss.map((s) => [s.id, s])));
    })();
  }, [open, rollIds]);

  const origin = window.location.origin;

  const labels = useMemo(() => rolls.map((roll) => {
    const product = products[roll.product_id];
    if (!product) return null;
    const mfr = manufacturers[product.manufacturer_id];
    const ser = series[product.series_id];
    const scanTarget = `${origin}/rolls/${roll.id}?action=cut`;
    return { key: roll.id, roll, product, manufacturer: mfr, series: ser, scanTarget };
  }).filter(Boolean), [rolls, products, manufacturers, series, origin]);

  // Pre-render QR codes to PNG data URLs so PDF generation stays synchronous
  // (keeps window.open inside the click gesture on mobile Safari/Chrome).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        labels.map(async (l) => {
          const url = await QRCode.toDataURL(l.scanTarget || l.roll.sku, {
            margin: 0, width: 512, errorCorrectionLevel: "M",
          });
          return [l.roll.id, url];
        })
      );
      if (!cancelled) setQrImages(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [labels]);

  const makePdf = () => buildLabelsPdf(labels, codeType, qrImages);

  const onPrint = () => {
    if (labels.length === 0) return;
    printLabelsHtml(labels, codeType, qrImages);
  };

  const onDownload = () => {
    if (labels.length === 0) return;
    makePdf().save("vwa-labels.pdf");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Printer size={20} /> Print Labels — 62 × 100 mm</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-4 py-3 border-y border-slate-200">
          <div className="text-sm">
            <span className="font-medium">{labels.length}</span>
            <span className="text-slate-500"> label{labels.length !== 1 ? "s" : ""} ready</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Code type</span>
            <ToggleGroup type="single" value={codeType} onValueChange={(v) => v && setCodeType(v)}>
              <ToggleGroupItem value="qr" aria-label="QR" data-testid="code-type-qr">
                <QrCode size={16} className="mr-1.5" /> QR
              </ToggleGroupItem>
              <ToggleGroupItem value="barcode" aria-label="Barcode" data-testid="code-type-barcode">
                <BarcodeIcon size={16} className="mr-1.5" /> Code128
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        {codeType === "qr" && (
          <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded-md p-2.5">
            QR encodes a deep link — scanning while logged in opens the Record Cut dialog for that roll.
          </div>
        )}
        <div className="text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded-md p-2.5">
          Prints straight to your device's print sheet at an exact <strong>62 × 100 mm</strong> — one tap, no download or Share menu. Keep the print scale at <strong>100%</strong>. Use <strong>Download PDF</strong> as a backup.
        </div>

        <div data-testid="labels-printable-area" className="grid grid-cols-2 lg:grid-cols-3 gap-4 py-3 bg-slate-50 p-4 rounded-md max-h-[60vh] overflow-y-auto">
          {labels.map((l) => (
            <RollLabel
              key={l.key}
              roll={l.roll}
              product={l.product}
              manufacturer={l.manufacturer}
              series={l.series}
              codeType={codeType}
              scanTarget={l.scanTarget}
            />
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md gap-2">
            <X size={16} /> Close
          </Button>
          <Button variant="outline" onClick={onDownload} disabled={labels.length === 0} className="rounded-md gap-2" data-testid="download-labels-pdf-btn">
            <DownloadSimple size={16} /> Download PDF
          </Button>
          <Button onClick={onPrint} disabled={labels.length === 0} className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2" data-testid="print-labels-btn">
            <Printer size={16} /> Print {labels.length} {labels.length === 1 ? "label" : "labels"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
